/**
 * Bergep (Rental Equipment) Hooks
 * Epic 13: Bérgép Management
 */

import {
  fetchAccessoriesPaginated,
  fetchAccessoryCatalog,
  fetchEquipmentAccessories,
  fetchFrequencySuggestedAccessories,
  getBergepById,
  getBergepList,
  getBergepRentalHistory,
  type AccessoryDto,
  type AccessoryListResponse105,
  type BergepAccessory,
  type BergepFilters,
  type BergepItem,
  type BergepListResponse,
  type RentalAccessoryCatalogItem,
  type RentalHistoryResponse,
  type SuggestedAccessoriesResponse105,
  type SuggestedAccessoryDto,
} from '@/api/rental-equipment';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseBergepResult {
  equipment: BergepItem[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseBergepItemResult {
  equipment: BergepItem | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseBergepRentalHistoryResult {
  history: RentalHistoryResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch list of rental equipment with filters
 */
export function useBergep(filters: BergepFilters = {}): UseBergepResult {
  const [equipment, setEquipment] = useState<BergepItem[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 20 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    status,
    category,
    condition,
    brand,
    search,
    availableOnly,
    maintenanceDueSoon,
    page,
    pageSize,
  } = filters;

  const fetchEquipment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: BergepFilters = {
        status,
        category,
        condition,
        brand,
        search,
        availableOnly,
        maintenanceDueSoon,
        page,
        pageSize,
      };
      const response: BergepListResponse = await getBergepList(params);
      setEquipment(response.data);
      setMeta(response.meta);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült betölteni a bérgépeket';
      setError(message);
      setEquipment([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    status,
    category,
    condition,
    brand,
    search,
    availableOnly,
    maintenanceDueSoon,
    page,
    pageSize,
  ]);

  useEffect(() => {
    void fetchEquipment();
  }, [fetchEquipment]);

  return {
    equipment,
    total: meta.total,
    page: meta.page,
    pageSize: meta.pageSize,
    isLoading,
    error,
    refetch: fetchEquipment,
  };
}

/**
 * Hook to fetch single equipment by ID
 */
export function useBergepItem(id: string | undefined): UseBergepItemResult {
  const [equipment, setEquipment] = useState<BergepItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEquipment = useCallback(async () => {
    if (!id) {
      setEquipment(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getBergepById(id);
      setEquipment(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült betölteni a bérgépet';
      setError(message);
      setEquipment(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchEquipment();
  }, [fetchEquipment]);

  return {
    equipment,
    isLoading,
    error,
    refetch: fetchEquipment,
  };
}

/**
 * Hook to fetch equipment rental history
 */
export function useBergepRentalHistory(
  equipmentId: string | undefined,
  page = 1,
  pageSize = 20
): UseBergepRentalHistoryResult {
  const [history, setHistory] = useState<RentalHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!equipmentId) {
      setHistory(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getBergepRentalHistory(equipmentId, page, pageSize);
      setHistory(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült betölteni az előzményeket';
      setError(message);
      setHistory(null);
    } finally {
      setIsLoading(false);
    }
  }, [equipmentId, page, pageSize]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    isLoading,
    error,
    refetch: fetchHistory,
  };
}

// ============================================
// ACCESSORIES HOOK (Story 57-2)
// ============================================

interface UseEquipmentAccessoriesResult {
  accessories: BergepAccessory[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ============================================
// 105-8b: useSuggestedAccessories + useAccessoriesPaginated
// ============================================

// D2 rename: source type follows backend meta.source enum
type SuggestedAccessorySource = 'required_sort' | 'default' | 'frequency';

interface UseSuggestedAccessoriesResult {
  suggestions: SuggestedAccessoryDto[];
  source: SuggestedAccessorySource | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * 105-8b: Fetch top-N suggested accessories for an equipment (AC-3).
 * Current proxy: isRequired DESC + value DESC (source='required_sort' when recent history exists,
 * 'default' otherwise). Full frequency aggregation ('frequency') arrives with 105-8b-v2 junction.
 */
export function useSuggestedAccessories(
  equipmentId: string | null,
  limit = 8
): UseSuggestedAccessoriesResult {
  const [suggestions, setSuggestions] = useState<SuggestedAccessoryDto[]>([]);
  const [source, setSource] = useState<SuggestedAccessorySource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(
    async (signal?: AbortSignal) => {
      if (!equipmentId) {
        setSuggestions([]);
        setSource(null);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const resp: SuggestedAccessoriesResponse105 = await fetchFrequencySuggestedAccessories(
          equipmentId,
          limit,
          signal
        );
        if (signal?.aborted) return;
        setSuggestions(resp.data);
        setSource(resp.meta.source);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        const message = err instanceof Error ? err.message : 'Suggested accessories fetch failed';
        setError(message);
        setSuggestions([]);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [equipmentId, limit]
  );

  useEffect(() => {
    // AbortController guards against stale responses when equipmentId changes mid-flight
    const controller = new AbortController();
    void fetch(controller.signal);
    return () => controller.abort();
  }, [fetch]);

  return { suggestions, source, isLoading, error, refetch: () => fetch() };
}

interface UseAccessoriesPaginatedResult {
  accessories: AccessoryDto[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseAccessoriesPaginatedOpts {
  search?: string;
  requiredOnly?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * 105-8b: Fetch paginated accessory list for the AccessoryDrawer (AC-7).
 * Server-side search (ILIKE), with requiredOnly filter.
 */
export function useAccessoriesPaginated(
  equipmentId: string | null,
  opts: UseAccessoriesPaginatedOpts = {}
): UseAccessoriesPaginatedResult {
  const [accessories, setAccessories] = useState<AccessoryDto[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 50 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { search, requiredOnly, page, pageSize } = opts;

  const fetch = useCallback(
    async (signal?: AbortSignal) => {
      if (!equipmentId) {
        setAccessories([]);
        // Reset meta so stale pagination controls don't render after equipment clear
        setMeta({ total: 0, page: 1, pageSize: pageSize ?? 50 });
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const resp: AccessoryListResponse105 = await fetchAccessoriesPaginated(
          equipmentId,
          { search, requiredOnly, page, pageSize },
          signal
        );
        if (signal?.aborted) return;
        setAccessories(resp.data);
        setMeta(resp.meta);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        const message = err instanceof Error ? err.message : 'Accessories fetch failed';
        setError(message);
        setAccessories([]);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [equipmentId, search, requiredOnly, page, pageSize]
  );

  useEffect(() => {
    // AbortController guards against stale responses on rapid equipment/search changes
    const controller = new AbortController();
    void fetch(controller.signal);
    return () => controller.abort();
  }, [fetch]);

  return {
    accessories,
    total: meta.total,
    page: meta.page,
    pageSize: meta.pageSize,
    isLoading,
    error,
    refetch: () => fetch(),
  };
}

// ============================================
// ACCESSORIES HOOK (Story 57-2)
// ============================================

/**
 * Hook to fetch accessories for a specific equipment item
 */
export function useEquipmentAccessories(equipmentId: string | null): UseEquipmentAccessoriesResult {
  const [accessories, setAccessories] = useState<BergepAccessory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccessories = useCallback(async () => {
    if (!equipmentId) {
      setAccessories([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchEquipmentAccessories(equipmentId);
      setAccessories(response.accessories ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült betölteni a tartozékokat';
      setError(message);
      setAccessories([]);
    } finally {
      setIsLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    void fetchAccessories();
  }, [fetchAccessories]);

  return {
    accessories,
    isLoading,
    error,
    refetch: fetchAccessories,
  };
}

// ============================================
// ACCESSORY CATALOG HOOK (feat/frontend-accessory-catalog-hook)
// Locked contract: Discord 2026-06-05 11:48 (Zeno)
// ============================================

interface UseAccessoriesForEquipmentResult {
  accessories: RentalAccessoryCatalogItem[];
  categories: string[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch the accessory catalog for a given equipment (MSW day-1-unblock).
 * equipmentId undefined → returns empty state, no fetch.
 * AbortController guards against stale responses on rapid equipmentId changes.
 *
 * SYNC-SOURCE: @kgc/api-contract rental-accessory-catalog.schema (Zeno BE-authored, pending)
 * — swap importra amint landol.
 */
export function useAccessoriesForEquipment(
  equipmentId: string | undefined
): UseAccessoriesForEquipmentResult {
  const [accessories, setAccessories] = useState<RentalAccessoryCatalogItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(
    async (signal?: AbortSignal) => {
      if (!equipmentId) {
        setAccessories([]);
        setCategories([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const resp = await fetchAccessoryCatalog(equipmentId, signal);
        if (signal?.aborted) return;
        setAccessories(resp.data);
        setCategories(resp.meta.categories);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        const message = err instanceof Error ? err.message : 'Accessory catalog fetch failed';
        setError(message);
        setAccessories([]);
        setCategories([]);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [equipmentId]
  );

  useEffect(() => {
    // AbortController guards against stale responses when equipmentId changes mid-flight
    const controller = new AbortController();
    void fetch(controller.signal);
    return () => controller.abort();
  }, [fetch]);

  return { accessories, categories, isLoading, error, refetch: () => fetch() };
}
